import {
  ClickAwayListener,
  MenuList,
  Paper,
  Divider,
  Typography,
  useTheme
} from '@mui/material'
import ListItemIcon from '@mui/material/ListItemIcon/ListItemIcon'
import ListItemText from '@mui/material/ListItemText/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import React, { useEffect, useState } from 'react'
import { t } from 'i18next'

import { DocType, EventDoc, GroupDoc } from 'types/api/changes'
import { useEventActions } from 'components/providers/EventActionsProvider'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { googleAttendeeStatusToGroupcal } from 'components/eventComponents/GoingBar'
import { isConvertedGroupId } from 'utils/groups'
import { CheckmarkIcon } from 'assets/icons'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { is24meApp } from 'utils/appType'
import { Tier } from 'utils/api/accounts'
import {
  iconSX,
  menuItemStyle,
  menuItemTextStyle
} from './GroupItemContextMenu'
import {
  EventMenuAccept,
  EventMenuDecline,
  EventMenuDelete,
  EventMenuDuplicate,
  EventMenuMaybe,
  EventMenuShare
} from 'assets/icons/event-menu'
import { GroupMenuArchive } from 'assets/icons/group-menu'

interface EventContextMenuProps {
  x: number
  y: number
  onClose: () => void
  event: EventDoc | null
  group: GroupDoc | undefined
  instanceStart: number
  instanceEnd: number
}

const EventContextMenu: React.FC<EventContextMenuProps> = ({
  x,
  y,
  onClose,
  event,
  group,
  instanceStart,
  instanceEnd
}) => {
  const theme = useTheme()
  const {
    deleteEvent,
    duplicateEvent,
    shareEvent,
    shouldShowGoingBar,
    changeRSVPStatus,
    setIsContextMenuOpen
  } = useEventActions()

  useEffect(() => {
    setIsContextMenuOpen(true)
    return () => {
      setIsContextMenuOpen(false)
    }
  }, [setIsContextMenuOpen])

  const handleDuplicate = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onClose()
    if (!event) return
    duplicateEvent(event, instanceStart, instanceEnd)
  }

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (event) {
      await shareEvent(event)
    }
    onClose()
  }

  const handleArchive = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onClose()
    if (event) {
      deleteEvent(event, group, instanceStart, instanceEnd)
    }
  }

  const handleRSVP = (e: React.MouseEvent, status: string) => {
    e.preventDefault()
    e.stopPropagation()
    onClose()
    if (event) {
      changeRSVPStatus(event, status)
    }
  }

  let type = t('event', { count: 1 }).toLowerCase()

  if (event?.Type === DocType.TASK) {
    type = t('task', { count: 1 }).toLowerCase()
  } else if (event?.Type === DocType.NOTE) {
    type = t('note', { count: 1 }).toLowerCase()
  }

  const menuWidthEstimate = 250
  const menuHeightEstimate = 300

  const isCloseToRight =
    typeof window !== 'undefined' && x + menuWidthEstimate > window.innerWidth

  const isCloseToBottom =
    typeof window !== 'undefined' && y + menuHeightEstimate > window.innerHeight

  const isThirdParty = isConvertedGroupId(event?.GroupID ?? '')

  let userStatus =
    event?.ParticipantsStatus?.[LocalStorage.get(LocalStorageKey.USER_ID) ?? '']
      ?.ParticipantConfirmationStatus ?? '1'

  if (event?.attendees && isThirdParty) {
    userStatus = googleAttendeeStatusToGroupcal(
      event.attendees?.find((a) => a.self)?.responseStatus
    )
  }

  const [selectedState, setSelectedState] = useState<string>(
    userStatus === '1' ? '4' : userStatus
  )

  if (!event || event.someday === '1') {
    return null
  }

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={onClose}
    >
      <Paper
        sx={{
          position: 'fixed',
          backgroundColor: theme.palette.card.primary,
          color: theme.palette.text.primary,
          top: isCloseToBottom ? undefined : y,
          bottom: isCloseToBottom ? window.innerHeight - y : undefined,
          left: isCloseToRight ? undefined : x,
          right: isCloseToRight ? window.innerWidth - x : undefined,
          borderRadius: '20px',
          padding: '0px 8px',
          zIndex: 9999,
          width: 'fit-content',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0px 10px 30px rgba(0, 0, 0, 0.35)'
              : '0px 4px 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        <MenuList>
          <MenuItem
            sx={menuItemStyle}
            onClick={handleArchive}
            id="event-menu-item-delete"
          >
            <ListItemIcon sx={iconSX}>
              {event?.Type === DocType.EVENT ? (
                <EventMenuDelete />
              ) : (
                <GroupMenuArchive />
              )}
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ sx: menuItemTextStyle }}>
              {event?.Type === DocType.EVENT ? t('delete') : t('archive')}
            </ListItemText>
          </MenuItem>
          <MenuItem sx={menuItemStyle}>
            <ListItemIcon sx={iconSX}>
              <EventMenuDuplicate />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ sx: menuItemTextStyle }}>
              <PopupSectionRow
                onlyTitle
                title={
                  <Typography fontSize={'14px'}>{t('duplicate')}</Typography>
                }
                clickableTitle
                neededTier={is24meApp() ? Tier.PRO : undefined}
                action={() => {
                  onClose()
                  if (!event) return
                  duplicateEvent(event, instanceStart, instanceEnd)
                }}
              />
              {}
            </ListItemText>
          </MenuItem>
          <Divider sx={{ ml: 1, borderColor: theme.palette.border.primary }} />
          <MenuItem sx={menuItemStyle} onClick={handleShare}>
            <ListItemIcon sx={iconSX}>
              <EventMenuShare />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ sx: menuItemTextStyle }}>
              {t('shareDetailsOf', { type: type })}
            </ListItemText>
          </MenuItem>
          {event &&
            shouldShowGoingBar(event) && [
              <Divider
                key="divider-rsvp"
                sx={{ ml: 1, borderColor: theme.palette.border.primary }}
              />,
              <MenuItem
                key="accept"
                sx={menuItemStyle}
                onClick={(e) => handleRSVP(e, '2')}
              >
                <ListItemIcon sx={iconSX}>
                  <EventMenuAccept />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ sx: menuItemTextStyle }}
                >
                  {t('accept')}
                </ListItemText>
                {selectedState === '2' && (
                  <ListItemIcon sx={{ ml: 2, justifyContent: 'flex-end' }}>
                    <CheckmarkIcon />
                  </ListItemIcon>
                )}
              </MenuItem>,
              <MenuItem
                key="maybe"
                sx={menuItemStyle}
                onClick={(e) => handleRSVP(e, '4')}
              >
                <ListItemIcon sx={iconSX}>
                  <EventMenuMaybe />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ sx: menuItemTextStyle }}
                >
                  {t('maybe')}
                </ListItemText>
                {selectedState === '4' && (
                  <ListItemIcon sx={{ ml: 2, justifyContent: 'flex-end' }}>
                    <CheckmarkIcon />
                  </ListItemIcon>
                )}
              </MenuItem>,
              <MenuItem
                key="decline"
                sx={menuItemStyle}
                onClick={(e) => handleRSVP(e, '3')}
              >
                <ListItemIcon sx={iconSX}>
                  <EventMenuDecline />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ sx: menuItemTextStyle }}
                >
                  {t('decline')}
                </ListItemText>
                {selectedState === '3' && (
                  <ListItemIcon sx={{ ml: 2, justifyContent: 'flex-end' }}>
                    <CheckmarkIcon />
                  </ListItemIcon>
                )}
              </MenuItem>
            ]}
        </MenuList>
      </Paper>
    </ClickAwayListener>
  )
}

export default EventContextMenu
